import React, { useEffect, useRef, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { useSearchEtablissements } from '@teetsh/app/src/queries/etablissements/useSearchEtablissements';
import { Etablissement } from '@teetsh/app/src/types/etablissement';
import {
  ArrowPathIcon as Refresh,
  MagnifyingGlassIcon as Search,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import tw, { TwStyle } from 'twin.macro';
import PortalWrapper from '@teetsh/app/src/components/PortalWrapper';
import { menuItemsStyle } from '@teetsh/app/src/components/Menu/MenuItems';
import {
  autoUpdate,
  flip,
  offset,
  size as floatingSize,
  useFloating,
} from '@floating-ui/react-dom';
import { inputRootStyle } from '@teetsh/app/src/components/Form/Input2';
import { css } from '@emotion/react';
import { boldMatchingText } from '@teetsh/app/src/utils/text/boldMatchingText';
import { SelectedSchool } from './SelectedSchool';
import { highlightStyle } from 'components/Typography/HighlightQuote';
import { useDebouncedValue } from 'utils/useDebouncedValue';
import {
  getEtablissementIdentifier,
  getEtablissementCity,
  getEtablissementPostalCode,
  getEtablissementAddress,
} from './etablissementHelpers';

interface SearchResult {
  query: string;
  selectedEtablissement?: Etablissement;
  nbResults: number;
}

const sizes = ['base', 'lg', 'xl'] as const;
type Size = (typeof sizes)[number];

interface Props {
  size: Size;
  large?: boolean;
  extraLarge?: boolean;
  onSelect: (v: SearchResult) => void;
  selectedEtablissementId?: string;
  onManual?: () => void;
  country: string;
  state?: string;
  label?: string;
  helperText?: string;
}

const sizeToIconSize: Record<Size, TwStyle> = {
  base: tw`h-5 w-5`,
  lg: tw`h-5 w-5`,
  xl: tw`h-7 w-7`,
};

function ManualAddOption({
  onClick,
  label,
}: {
  onClick?: () => void;
  label: string;
}) {
  if (!onClick) {
    return null;
  }
  return (
    <Combobox.Option
      value={null}
      onClick={onClick}
      tw="px-4 py-3 cursor-pointer text-neutral-moderate hover:bg-neutral-supershy border-t border-neutral-quiet"
    >
      <div tw="flex items-center gap-2.5 w-full">
        <PlusIcon tw="w-5 h-5" />
        <span tw="font-medium">{label}</span>
      </div>
    </Combobox.Option>
  );
}

export default function SearchEtablissement(props: Props) {
  const {
    onSelect,
    onManual,
    selectedEtablissementId,
    size,
    label,
    helperText,
    country,
    state,
  } = props;
  const { t } = useTranslation(['schoolyearSettings']);
  const [query, setQuery] = useState('');
  const [selectedEtablissement, setSelectedEtablissement] = useState<
    Etablissement | undefined
  >(undefined);

  const [debouncedQuery] = useDebouncedValue(query);

  const { data: etablissements = [], isLoading } = useSearchEtablissements({
    query: debouncedQuery || selectedEtablissementId || '',
    country,
    state,
  });
  const preselectedEtablissement = etablissements.find(
    e => e.id === selectedEtablissementId,
  );

  useEffect(() => {
    // search etablissement by selected id, then select it if we find it and if no etablissement is currently selected
    if (preselectedEtablissement && !selectedEtablissement) {
      setSelectedEtablissement(preselectedEtablissement);
    }
  }, [preselectedEtablissement?.id]);

  const comboboxRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
      offset(4),
      flip(),
      floatingSize({
        apply({ availableWidth, elements, rects }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            minWidth: `${rects.reference.width}px`,
            maxWidth: `${availableWidth}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  function handleSelection(etablissement: Etablissement | null) {
    if (!etablissement) return;

    setSelectedEtablissement(etablissement);
    onSelect({
      query: debouncedQuery,
      selectedEtablissement: etablissement,
      nbResults: etablissements.length,
    });

    // Close dropdown and clear search query when a school is selected
    setQuery('');
  }

  function handleClearSelection() {
    setSelectedEtablissement(undefined);
    onSelect({
      query: '',
      selectedEtablissement: undefined,
      nbResults: 0,
    });
    setQuery('');
  }

  if (selectedEtablissement) {
    return (
      <SelectedSchool
        etablissement={selectedEtablissement}
        onClear={handleClearSelection}
      />
    );
  }

  return (
    <div data-testid="search-etablissement">
      {label && (
        <div tw="block text-sm font-medium text-neutral-intense mb-2">
          {label}
        </div>
      )}
      <Combobox value={null} onChange={handleSelection}>
        {/* Use Combobox value={null} to prevent auto selection on click outside */}
        <div tw="relative" ref={comboboxRef}>
          <div tw="relative" ref={refs.setReference}>
            <div tw="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {isLoading ? (
                <Refresh
                  css={sizeToIconSize[size]}
                  tw="animate-spin text-neutral-quiet"
                />
              ) : (
                <Search css={sizeToIconSize[size]} tw="text-neutral-quiet" />
              )}
            </div>
            <Combobox.Input
              value={query}
              onChange={ev => setQuery(ev.target.value)}
              placeholder={t(
                'schoolyearSettings:schoolGeneral.searchSchool.placeholder',
              )}
              autoFocus
              css={[
                inputRootStyle({
                  large: size === 'lg',
                  extraLarge: size === 'xl',
                }),
                tw`w-full pl-10`,
              ]}
            />
          </div>

          {/* Dropdown */}
          <PortalWrapper portal={true}>
            <div ref={refs.setFloating} style={floatingStyles} tw="z-50">
              <div ref={dropdownRef}>
                <Transition
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Combobox.Options css={[menuItemsStyle, tw`flex flex-col`]}>
                    {etablissements.length === 0 && !isLoading && (
                      <div tw="px-4 py-3 flex justify-center items-center">
                        <span tw="text-neutral-quiet text-base">
                          {t(
                            'schoolyearSettings:schoolGeneral.searchSchool.noResults',
                          )}
                        </span>
                      </div>
                    )}
                    <div
                      tw="max-h-60 overflow-auto flex-1"
                      css={css`
                        strong {
                          ${tw`font-bold`}
                          ${highlightStyle}
                        }
                      `}
                    >
                      {etablissements.map(e => {
                        const identifier = getEtablissementIdentifier(e);
                        const address = getEtablissementAddress(e);
                        const postalCode = getEtablissementPostalCode(e);
                        const city = getEtablissementCity(e);
                        return (
                          <Combobox.Option
                            key={e.id}
                            value={e}
                            css={[
                              tw`px-4 py-3 flex justify-between items-start cursor-pointer hover:bg-neutral-supershy`,
                              css`
                                &[data-headlessui-state~='active'] {
                                  ${tw`bg-neutral-supershy`}
                                }
                              `,
                            ]}
                          >
                            <div tw="flex-grow">
                              <div tw="flex justify-between items-start mb-2">
                                <div
                                  tw="font-semibold text-neutral-intense text-base flex-grow"
                                  dangerouslySetInnerHTML={{
                                    __html: boldMatchingText(e.name, query),
                                  }}
                                />
                                {identifier && (
                                  <div tw="text-sm text-neutral-quiet ml-4 whitespace-nowrap">
                                    {'uai' in e &&
                                      t(
                                        'schoolyearSettings:schoolGeneral.searchSchool.nationalIdentifier',
                                      )}
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: boldMatchingText(
                                          identifier,
                                          query,
                                        ),
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                              <div tw="text-sm text-neutral-quiet">
                                {address && <span>{address} </span>}
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: boldMatchingText(postalCode, query),
                                  }}
                                />
                                {postalCode && city && ', '}
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: boldMatchingText(city, query),
                                  }}
                                />
                              </div>
                            </div>
                          </Combobox.Option>
                        );
                      })}
                    </div>
                    <ManualAddOption
                      onClick={onManual}
                      label={t(
                        'schoolyearSettings:schoolGeneral.searchSchool.switchToManual',
                      )}
                    />
                  </Combobox.Options>
                </Transition>
              </div>
            </div>
          </PortalWrapper>
        </div>
      </Combobox>
      {helperText && <p tw="mt-2 text-sm text-neutral-quiet">{helperText}</p>}
    </div>
  );
}
